import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form, Select, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

export type SelectPayrollFilterValues = {
  timesheetId?: string;
  startDate?: string;
  endDate?: string;
  batchFrom?: number;
  batchTo?: number;
};

type SelectPayrollFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: SelectPayrollFilterValues) => void;
};

const FILTER_OPTIONS = [
  { label: 'Timesheet Id', value: 'timesheetId' },
  { label: 'Week End Date', value: 'endDate' },
  { label: 'Batch #', value: 'batch' },
];

const SelectPayrollFilterModal: React.FC<SelectPayrollFilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);

  const selectedReportBy: string[] = watchValue?.reportBy ?? [];

  const ableToClearFilter =
    watchValue &&
    Object.keys(watchValue).some(
      (key) => key !== 'reportBy' && !!watchValue[key],
    );

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params: SelectPayrollFilterValues = {};
    if (values.timesheetId) params.timesheetId = values.timesheetId;
    if (values.weekEndDateFrom)
      params.startDate = moment(values.weekEndDateFrom).format('MM/DD/YYYY');
    if (values.weekEndDateTo)
      params.endDate = moment(values.weekEndDateTo).format('MM/DD/YYYY');
    if (values.batchFrom) params.batchFrom = values.batchFrom;
    if (values.batchTo) params.batchTo = values.batchTo;
    onFilter(params);
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item name="reportBy" label="Report By">
          <Select
            mode="multiple"
            placeholder={messages['form.select']}
            options={FILTER_OPTIONS}
            allowClear
          />
        </Form.Item>

        {selectedReportBy.includes('timesheetId') && (
          <Form.Item
            name="timesheetId"
            label={messages['timesheet.timesheetId']}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        )}

        {selectedReportBy.includes('batch') && (
          <>
            <Typography.Text>{messages['timesheet.batch']}</Typography.Text>
            <div className="grid grid-cols-2 gap-x-4 mt-2">
              <Form.Item name="batchFrom">
                <AppRateInput placeholder={messages['common.from']} />
              </Form.Item>
              <Form.Item name="batchTo">
                <AppRateInput placeholder={messages['common.to']} />
              </Form.Item>
            </div>
          </>
        )}

        {selectedReportBy.includes('endDate') && (
          <>
            <Typography.Text>
              {messages['timesheet.weekEndDate']}
            </Typography.Text>
            <div className="grid grid-cols-2 gap-x-4 mt-2">
              <Form.Item name="weekEndDateFrom">
                <AppDatePicker
                  placeholder={messages['common.fromDate']}
                  onChange={(date: Moment) => {
                    form.setFieldsValue({ weekEndDateFrom: date });
                  }}
                  format="DD/MM/YYYY"
                  maxDate={watchValue?.weekEndDateTo}
                />
              </Form.Item>
              <Form.Item name="weekEndDateTo">
                <AppDatePicker
                  placeholder={messages['common.toDate']}
                  onChange={(date: Moment) => {
                    form.setFieldsValue({ weekEndDateTo: date });
                  }}
                  format="DD/MM/YYYY"
                  minDate={watchValue?.weekEndDateFrom}
                />
              </Form.Item>
            </div>
          </>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default SelectPayrollFilterModal;
